import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Paper,
} from '@mui/material';
import {useGetProductLocationView} from '../../../hooks/productions/useProductions';
import {GetProductLocationViewRequestParam} from '../../../api/types/production.type';
import '../Demo.scss';

const ProductLocationViewApiTest: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [productionId, setProductionId] = useState<number>(235);
  const [moduleType, setModuleType] = useState<string>('PRODUCTION');
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const requestParams: GetProductLocationViewRequestParam = {
    SecurityGroupId: securityGroupId,
    productionId: productionId,
    moduleType: moduleType,
  };

  const {
    data: productLocationData,
    isLoading,
    error,
    refetch,
  } = useGetProductLocationView(requestParams, enabled);

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (productLocationData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(productLocationData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Product Location View API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Get product location view including production output location, folder
          details, root paths, and volume settings
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" data-testid={`product-location-view-chip-info`} />
            <Typography variant="h6" className="typo-text subhead2_size">
              Get Product Location View
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/view/productlocationview
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get product location view with production output location settings,
            folder details, root paths, volume settings, and archive options.
          </Typography>
        </CardContent>
      </Card>
      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Security Group ID"
              type="number"
              value={securityGroupId}
              onChange={(e) => setSecurityGroupId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Module Type"
              value={moduleType}
              onChange={(e) => setModuleType(e.target.value)}
              size="small"
              placeholder="e.g., PRODUCTION, EXPORT"
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestApi}
              data-testid="product-location-view-api-test-test-api-button">
              🚀 Test API
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              data-testid="product-location-view-api-test-reset-button">
              🔄 Reset
            </Button>
            {enabled && (
              <Button
                variant="outlined"
                onClick={() => refetch()}
                data-testid="product-location-view-api-test-refetch-button">
                ♻️ Refetch
              </Button>
            )}
            {productLocationData && (
              <Button
                variant="outlined"
                onClick={handleCopyJson}
                data-testid="product-location-view-api-test-button">
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Loading State */}
      {isLoading && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">⏳ Loading... Making API request...</Alert>
          </CardContent>
        </Card>
      )}
      {/* Error State */}
      {error && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {error.message || 'An error occurred while fetching data'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Response Data */}
      {productLocationData && !isLoading && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Response Data
              </Typography>

              {/* Basic Info */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Basic Information
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Box sx={{display: 'grid', gap: 1}}>
                    <Typography className="typo-text body3_size">
                      <strong>Module Type:</strong>{' '}
                      {productLocationData.moduleType}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Production ID:</strong>{' '}
                      {productLocationData.productionId}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Security Group ID:</strong>{' '}
                      {productLocationData.securityGroupId}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Is Mark Complete:</strong>{' '}
                      {productLocationData.isMarkComplete ? 'Yes' : 'No'}
                    </Typography>
                    {productLocationData.productionStatusCode && (
                      <Typography className="typo-text body3_size">
                        <strong>Production Status Code:</strong>{' '}
                        {productLocationData.productionStatusCode}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Box>

              {/* Production Output Location */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Production Output Location
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Box sx={{display: 'grid', gap: 1}}>
                    <Typography className="typo-text body3_size">
                      <strong>Root Path ID:</strong>{' '}
                      {
                        productLocationData.productionOutputLocation
                          .productionRootPathID
                      }
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Root Path Name:</strong>{' '}
                      {productLocationData.productionOutputLocation
                        .productionRootPathName || 'N/A'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Production Directory:</strong>{' '}
                      {productLocationData.productionOutputLocation
                        .productionDirectory || 'N/A'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Volume Name:</strong>{' '}
                      {productLocationData.productionOutputLocation.volumeName}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Drive Text:</strong>{' '}
                      {productLocationData.productionOutputLocation.driveText}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Is Volume Included:</strong>{' '}
                      {productLocationData.productionOutputLocation
                        .isVolumeIncluded
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Should Include Drive:</strong>{' '}
                      {productLocationData.productionOutputLocation
                        .shouldIncludeDrive
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Should Archive:</strong>{' '}
                      {productLocationData.productionOutputLocation
                        .shouldArchive
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              {/* Production Components Folder Details */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Production Components Folder Details
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Box sx={{display: 'grid', gap: 1}}>
                    <Typography className="typo-text body3_size">
                      <strong>Natives Folder:</strong>{' '}
                      {productLocationData.productionComponentsFolderDetails
                        .folderName_Natives || 'N/A'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>PDF Folder:</strong>{' '}
                      {productLocationData.productionComponentsFolderDetails
                        .folderName_PDF || 'N/A'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Text Folder:</strong>{' '}
                      {productLocationData.productionComponentsFolderDetails
                        .folderName_Text || 'N/A'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Images Folder:</strong>{' '}
                      {productLocationData.productionComponentsFolderDetails
                        .folderName_Images || 'N/A'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>MP3 Folder:</strong>{' '}
                      {productLocationData.productionComponentsFolderDetails
                        .folderName_MP3 || 'N/A'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Load Files Folder:</strong>{' '}
                      {productLocationData.productionComponentsFolderDetails
                        .folderName_LoadFiles || 'N/A'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Translations Folder:</strong>{' '}
                      {productLocationData.productionComponentsFolderDetails
                        .folderName_Translations || 'N/A'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>To Split Sub Folders:</strong>{' '}
                      {productLocationData.productionComponentsFolderDetails
                        .toSplitSubFolders
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                    <Typography className="typo-text body3_size">
                      <strong>Split Count:</strong>{' '}
                      {
                        productLocationData.productionComponentsFolderDetails
                          .splitCount
                      }
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              {/* Production Root Paths */}
              {productLocationData.lstProductionRootPath &&
                productLocationData.lstProductionRootPath.length > 0 && (
                  <Box sx={{marginBottom: 3}}>
                    <Typography
                      className="typo-text subhead3_size"
                      sx={{marginBottom: 1}}
                    >
                      Production Root Paths
                    </Typography>
                    <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                      {productLocationData.lstProductionRootPath.map(
                        (path, index) => (
                          <Box key={index} sx={{marginBottom: 1}}>
                            <Typography className="typo-text body3_size">
                              <strong>ID {path.productionRootId}:</strong>{' '}
                              {path.productionRootLabel}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Paper>
                  </Box>
                )}

              {/* Volume Location Options */}
              {productLocationData.volumenLocation && (
                <Box sx={{marginBottom: 3}}>
                  <Typography
                    className="typo-text subhead3_size"
                    sx={{marginBottom: 1}}
                  >
                    Volume Location Options
                  </Typography>
                  <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                    <Box sx={{display: 'grid', gap: 1}}>
                      {Object.entries(productLocationData.volumenLocation).map(
                        ([key, value]) => (
                          <Typography
                            key={key}
                            className="typo-text body3_size"
                          >
                            <strong>{key}:</strong> {value}
                          </Typography>
                        )
                      )}
                    </Box>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 600,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(productLocationData, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default ProductLocationViewApiTest;
